import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useUpsertRepairCloseInvoiceMutation, useLazyGetCustomerDiscountRatesQuery, useRepairClosingToAccountingMutation, useUndoRepairCloseAccountingMutation } from '../../../../../api';
import { useGetPreAccountingCustomersQuery, useGetPreAccountingKdvQuery } from '../../../../../api/endpoints/preAccounting';
import { Dropdown } from 'primereact/dropdown';

const RepairCloseDialog = ({ visible, onHide, data, customerStatementId, defaultCustomerId, isDomestic = false }) => {
    const { t } = useLanguage();
    const toast = useRef(null);
    const [labors, setLabors] = useState([]);
    const [parts, setParts] = useState([]);
    const [otherCosts, setOtherCosts] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceDate, setInvoiceDate] = useState(null);
    const [maturityDate, setMaturityDate] = useState(null);
    const [customerId, setCustomerId] = useState(null);
    const [totalPrice, setTotalPrice] = useState(0);
    const [initialCustomerId, setInitialCustomerId] = useState(null);
    const [isAccounting, setIsAccounting] = useState(false);
    const [repairCloseInvoiceId, setRepairCloseInvoiceId] = useState(null);
    const [isInvoiceCreated, setIsInvoiceCreated] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [shouldApplyDiscounts, setShouldApplyDiscounts] = useState(false);
    const [upsertRepairCloseInvoice, { isLoading: isSaving }] = useUpsertRepairCloseInvoiceMutation();
    const [repairClosingToAccounting, { isLoading: isCompletingInvoice }] = useRepairClosingToAccountingMutation();
    const [undoRepairCloseAccounting, { isLoading: isUndoingAccounting }] = useUndoRepairCloseAccountingMutation();
    const [getCustomerDiscountRates] = useLazyGetCustomerDiscountRatesQuery();

    const [customerFilterValue, setCustomerFilterValue] = useState('');
    const [customerSearchText, setCustomerSearchText] = useState('');
    const customerDebounceRef = useRef(null);

    // PreAccounting customer dropdown
    const { data: customerOptions = [], isLoading: isLoadingCustomers } = useGetPreAccountingCustomersQuery({ SearchText: customerSearchText });
    const { data: kdvRate } = useGetPreAccountingKdvQuery(undefined, { skip: !visible || !isDomestic });

    useEffect(() => {
        if (data && visible) {
            // Map labors
            const initialLabors = (data.labors || []).map((labor, idx) => ({
                ...labor,
                _rowKey: `labor-${idx}`,
                laborDiscountRate: labor.laborDiscountRate || 0,
                sellingPrice: labor.sellingPrice ?? 0,
                baseSellingPrice: labor.acceptedPrice ?? labor.laborPrice ?? 0
            }));

            // Map parts
            const initialParts = (data.parts || []).map((part, idx) => ({
                ...part,
                _rowKey: `part-${idx}`,
                partsDiscountRate: part.partsDiscountRate || 0,
                sellingPrice: part.sellingPrice ?? 0,
                baseSellingPrice: part.acceptedPrice ?? part.itemPrice ?? 0
            }));

            // Map other costs
            const initialOtherCosts = (data.otherCosts || []).map((cost, idx) => ({
                ...cost,
                _rowKey: `cost-${idx}`,
                sellingPrice: cost.sellingPrice ?? 0
            }));

            setLabors(initialLabors);
            setParts(initialParts);
            setOtherCosts(initialOtherCosts);

            // Set form fields from data
            setInvoiceNumber(data.invoiceNumber || '');
            setInvoiceDate(data.invoiceDate ? new Date(data.invoiceDate) : null);
            setMaturityDate(data.maturityDate ? new Date(data.maturityDate) : null);
            // API'den gelen totalAmount'u olduğu gibi kullan - hesaplama yapma
            setTotalPrice(data.totalAmount || 0);

            // Check if invoice is already created
            const invoiceAlreadyCreated = data.invoiceNumber && data.invoiceNumber.trim() !== '';
            setIsInvoiceCreated(invoiceAlreadyCreated);

            // Check accounting status - API'den isAccounted olarak geliyor
            setIsAccounting(data.isAccounted || data.isAccounting || false);

            // RepairCloseInvoice id - muhasebe geri alma işlemi için
            setRepairCloseInvoiceId(data.repairCloseInvoiceId ?? null);

            // Customer ID mantığı:
            // 1. Eğer API'den customerId geliyorsa onu kullan (indirim oranları zaten data'da var)
            // 2. Gelmiyorsa table'dan defaultCustomerId'yi al VE indirim oranlarını çek
            const hasCustomerFromApi = data.customerId != null;
            // Dropdown options string olarak geliyor, customerId'yi string'e çevir
            const customerIdToSet = hasCustomerFromApi ? String(data.customerId) : defaultCustomerId ? String(defaultCustomerId) : null;
            setCustomerId(customerIdToSet);

            // Eğer API'den customerId gelmiyorsa ve table'dan alıyorsak, indirim oranlarını uygula
            setShouldApplyDiscounts(!hasCustomerFromApi && customerIdToSet != null);

            // Remember initial customer ID
            setInitialCustomerId(customerIdToSet);

            // Reset unsaved changes flag when dialog opens with new data
            setHasUnsavedChanges(false);
        }
    }, [data, visible, defaultCustomerId]);

    const handleCustomerFilterChange = (val) => {
        setCustomerFilterValue(val);

        if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);

        customerDebounceRef.current = setTimeout(() => {
            setCustomerSearchText(val);
        }, 400);
    };

    const customerFilterTemplate = () => (
        <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
            <InputText
                value={customerFilterValue}
                onChange={(e) => handleCustomerFilterChange(e.target.value)}
                className="p-dropdown-filter w-full"
                placeholder={t('common.search') || 'Ara'}
                autoFocus
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    );
    useEffect(() => {
        return () => {
            if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
        };
    }, []);

    // İlk yüklemede indirim oranlarını uygula (eğer API'den customerId gelmediyse)
    useEffect(() => {
        const applyInitialDiscounts = async () => {
            if (shouldApplyDiscounts && customerId && labors.length === 0 && parts.length === 0) {
                try {
                    // customerId string olabilir, number'a çevir
                    const result = await getCustomerDiscountRates(Number(customerId)).unwrap();
                    if (result) {
                        const { partsDiscountRate, laborDiscountRate } = result;

                        // Update labor discount rates
                        const updatedLabors = labors.map((labor) => ({
                            ...labor,
                            laborDiscountRate: laborDiscountRate || 0,
                            sellingPrice: calculateSellingPrice(labor.baseSellingPrice ?? labor.sellingPrice ?? 0, laborDiscountRate)
                        }));
                        setLabors(updatedLabors);

                        // Update part discount rates
                        const updatedParts = parts.map((part) => ({
                            ...part,
                            partsDiscountRate: partsDiscountRate || 0,
                            sellingPrice: calculateSellingPrice(part.baseSellingPrice ?? part.sellingPrice ?? 0, partsDiscountRate)
                        }));
                        setParts(updatedParts);

                        setShouldApplyDiscounts(false);
                    }
                } catch (error) {
                    console.error('Initial discount rates fetch error:', error);
                }
            }
        };

        applyInitialDiscounts();
    }, [shouldApplyDiscounts, customerId, labors, parts, getCustomerDiscountRates]);

    // Manuel hesaplama fonksiyonu - sadece kullanıcı değişiklik yaptığında çağrılacak
    const calculateAndUpdateTotal = useCallback(() => {
        const laborTotal = labors.reduce((sum, labor) => sum + (labor.sellingPrice || 0), 0);
        const partTotal = parts.reduce((sum, part) => sum + (part.sellingPrice || 0), 0);
        const otherCostTotal = otherCosts.reduce((sum, cost) => sum + (cost.sellingPrice || 0), 0);
        const calculatedTotal = laborTotal + partTotal + otherCostTotal;
        setTotalPrice(calculatedTotal);
    }, [labors, parts, otherCosts]);

    // Handle customer change - fetch discount rates and update tables
    const handleCustomerChange = async (e) => {
        const newCustomerId = e.value;
        setCustomerId(newCustomerId);
        setHasUnsavedChanges(true);

        // Manuel seçimde her zaman indirim oranlarını uygula
        if (newCustomerId) {
            try {
                // newCustomerId string olabilir, number'a çevir
                const result = await getCustomerDiscountRates(Number(newCustomerId)).unwrap();

                if (result) {
                    const { partsDiscountRate, laborDiscountRate } = result;

                    // Update labor discount rates and recalculate selling prices
                    const updatedLabors = labors.map((labor) => {
                        const base = labor.baseSellingPrice ?? labor.sellingPrice ?? 0;
                        const newSellingPrice = calculateSellingPrice(base, laborDiscountRate);
                        return {
                            ...labor,
                            laborDiscountRate: laborDiscountRate || 0,
                            sellingPrice: newSellingPrice
                        };
                    });
                    setLabors(updatedLabors);

                    // Update part discount rates and recalculate selling prices
                    const updatedParts = parts.map((part) => {
                        const base = part.baseSellingPrice ?? part.sellingPrice ?? 0;
                        const newSellingPrice = calculateSellingPrice(base, partsDiscountRate);
                        return {
                            ...part,
                            partsDiscountRate: partsDiscountRate || 0,
                            sellingPrice: newSellingPrice
                        };
                    });
                    setParts(updatedParts);

                    // Güncellenmiş değerlerle direkt toplam hesapla
                    const laborTotal = updatedLabors.reduce((sum, labor) => sum + (labor.sellingPrice || 0), 0);
                    const partTotal = updatedParts.reduce((sum, part) => sum + (part.sellingPrice || 0), 0);
                    const otherCostTotal = otherCosts.reduce((sum, cost) => sum + (cost.sellingPrice || 0), 0);
                    setTotalPrice(laborTotal + partTotal + otherCostTotal);

                    toast.current?.show({
                        severity: 'success',
                        summary: t('common.success'),
                        detail: t('warranty.customerDeclaration.repairClose.discountRatesUpdated') || 'İndirim oranları güncellendi',
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('Discount rates fetch error:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: t('common.error'),
                    detail: error?.data?.errorMessage || t('common.error'),
                    life: 3000
                });
            }
        }
    };

    // Calculate selling price based on discount rate
    const calculateSellingPrice = (originalPrice, discountRate) => {
        if (!originalPrice || discountRate == null) return originalPrice;
        return originalPrice - (originalPrice * discountRate) / 100;
    };

    // Calculate discount rate based on selling price
    const calculateDiscountRate = (originalPrice, sellingPrice) => {
        if (!originalPrice || sellingPrice == null) return 0;
        return ((originalPrice - sellingPrice) / originalPrice) * 100;
    };

    // Handle cell edit complete for labors
    const onLaborCellEditComplete = (e) => {
        const { rowData, newValue, field } = e;

        const updatedLabors = labors.map((labor) => {
            if (labor._rowKey === rowData._rowKey) {
                const base = labor.baseSellingPrice ?? labor.sellingPrice ?? 0;
                if (field === 'laborDiscountRate') {
                    const newSellingPrice = calculateSellingPrice(base, newValue ?? labor.laborDiscountRate);
                    return {
                        ...labor,
                        laborDiscountRate: newValue ?? labor.laborDiscountRate,
                        sellingPrice: newSellingPrice
                    };
                } else if (field === 'sellingPrice') {
                    const safeValue = newValue ?? labor.sellingPrice;
                    const newDiscountRate = calculateDiscountRate(base, safeValue);
                    return {
                        ...labor,
                        sellingPrice: safeValue,
                        laborDiscountRate: newDiscountRate
                    };
                }
            }
            return labor;
        });
        setLabors(updatedLabors);
        setHasUnsavedChanges(true);

        const laborTotal = updatedLabors.reduce((sum, labor) => sum + (labor.sellingPrice || 0), 0);
        const partTotal = parts.reduce((sum, part) => sum + (part.sellingPrice || 0), 0);
        const otherCostTotal = otherCosts.reduce((sum, cost) => sum + (cost.sellingPrice || 0), 0);
        setTotalPrice(laborTotal + partTotal + otherCostTotal);
    };

    // Handle cell edit complete for parts
    const onPartCellEditComplete = (e) => {
        const { rowData, newValue, field } = e;

        const updatedParts = parts.map((part) => {
            if (part._rowKey === rowData._rowKey) {
                const base = part.baseSellingPrice ?? part.sellingPrice ?? 0;
                if (field === 'partsDiscountRate') {
                    const newSellingPrice = calculateSellingPrice(base, newValue ?? part.partsDiscountRate);
                    return {
                        ...part,
                        partsDiscountRate: newValue ?? part.partsDiscountRate,
                        sellingPrice: newSellingPrice
                    };
                } else if (field === 'sellingPrice') {
                    const safeValue = newValue ?? part.sellingPrice;
                    const newDiscountRate = calculateDiscountRate(base, safeValue);
                    return {
                        ...part,
                        sellingPrice: safeValue,
                        partsDiscountRate: newDiscountRate
                    };
                }
            }
            return part;
        });
        setParts(updatedParts);
        setHasUnsavedChanges(true);

        const laborTotal = labors.reduce((sum, labor) => sum + (labor.sellingPrice || 0), 0);
        const partTotal = updatedParts.reduce((sum, part) => sum + (part.sellingPrice || 0), 0);
        const otherCostTotal = otherCosts.reduce((sum, cost) => sum + (cost.sellingPrice || 0), 0);
        setTotalPrice(laborTotal + partTotal + otherCostTotal);
    };

    // Handle cell edit complete for other costs
    const onOtherCostCellEditComplete = (e) => {
        const { rowData, newValue, field } = e;

        const updatedOtherCosts = otherCosts.map((cost) => {
            if (cost._rowKey === rowData._rowKey) {
                return {
                    ...cost,
                    sellingPrice: newValue ?? cost.sellingPrice
                };
            }
            return cost;
        });
        setOtherCosts(updatedOtherCosts);
        setHasUnsavedChanges(true);

        const laborTotal = labors.reduce((sum, labor) => sum + (labor.sellingPrice || 0), 0);
        const partTotal = parts.reduce((sum, part) => sum + (part.sellingPrice || 0), 0);
        const otherCostTotal = updatedOtherCosts.reduce((sum, cost) => sum + (cost.sellingPrice || 0), 0);
        setTotalPrice(laborTotal + partTotal + otherCostTotal);
    };

    // Validate discount rate
    const validateDiscountRate = (value) => {
        if (value < 0 || value > 100) {
            toast.current?.show({
                severity: 'error',
                summary: t('common.error'),
                detail: t('warranty.customerDeclaration.repairClose.discountRateOutOfRange') || 'İndirim oranı 0 ile 100 arasında olmalıdır',
                life: 3000
            });
            return false;
        }
        return true;
    };

    // Editor for discount rate
    const discountEditor = (options) => {
        return (
            <InputNumber
                value={options.value || 0}
                onValueChange={(e) => {
                    const newValue = e.value || 0;
                    if (validateDiscountRate(newValue)) {
                        options.editorCallback(newValue);
                    }
                }}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                min={0}
                max={100}
                suffix="%"
                style={{ width: '100%' }}
                autoFocus
            />
        );
    };

    // Editor for selling price
    const priceEditor = (options) => {
        return <InputNumber value={options.value || 0} onValueChange={(e) => options.editorCallback(e.value)} mode="decimal" minFractionDigits={2} maxFractionDigits={2} min={0} style={{ width: '100%' }} autoFocus />;
    };

    const handleSave = async () => {
        try {
            // Validate required fields
            if (!customerId) {
                toast.current?.show({
                    severity: 'warn',
                    summary: t('common.warning'),
                    detail: t('warranty.customerDeclaration.repairClose.customerRequired') || 'Müşteri seçimi zorunludur',
                    life: 3000
                });
                return;
            }

            if (!invoiceDate) {
                toast.current?.show({
                    severity: 'warn',
                    summary: t('common.warning'),
                    detail: t('warranty.customerDeclaration.repairClose.invoiceDateRequired') || 'Fatura tarihi zorunludur',
                    life: 3000
                });
                return;
            }

            // Prepare data for API
            const requestData = {
                customerStatementId: customerStatementId || 0,
                invoiceNumber: invoiceNumber || '',
                customerId: customerId != null ? Number(customerId) : null,
                invoiceDate: invoiceDate ? invoiceDate.toISOString() : new Date().toISOString(),
                maturityDate: maturityDate ? maturityDate.toISOString() : new Date().toISOString(),
                totalAmount: totalPrice || 0,
                partList: parts.map((part) => ({
                    id: part.id,
                    name: part.description || '',
                    code: part.itemCode || '',
                    discountRate: part.partsDiscountRate || 0,
                    amount: part.sellingPrice || 0
                })),
                laborList: labors.map((labor) => ({
                    id: labor.id,
                    name: labor.description || '',
                    code: labor.laborCode || '',
                    discountRate: labor.laborDiscountRate || 0,
                    amount: labor.sellingPrice || 0
                })),
                otherCostList: otherCosts.map((cost) => ({
                    id: cost.id,
                    name: cost.description || '',
                    code: cost.costType || '',
                    discountRate: 0,
                    amount: cost.sellingPrice || 0
                }))
            };

            await upsertRepairCloseInvoice(requestData).unwrap();

            // Mark invoice as created after successful save
            setIsInvoiceCreated(true);

            // Reset unsaved changes flag after successful save
            setHasUnsavedChanges(false);

            toast.current?.show({
                severity: 'success',
                summary: t('common.success'),
                detail: t('warranty.customerDeclaration.repairClose.saveSuccess') || 'Kayıt başarılı',
                life: 3000
            });
        } catch (error) {
            console.error('Kayıt hatası:', error);
            const errorMessage = error?.data?.errorMessage || error?.data?.message || error?.message || t('common.error');
            toast.current?.show({
                severity: 'error',
                summary: t('common.error'),
                detail: errorMessage,
                life: 3000
            });
        }
    };

    const handleCompleteInvoice = async () => {
        try {
            await repairClosingToAccounting(customerStatementId).unwrap();

            toast.current?.show({
                severity: 'success',
                summary: t('common.success'),
                detail: t('warranty.customerDeclaration.repairClose.invoiceCompleted') || 'Fatura ön muhasebeye gönderildi',
                life: 3000
            });

            // Close dialog after success
            setTimeout(() => {
                onHide();
            }, 1000);
        } catch (error) {
            console.error('Fatura tamamlama hatası:', error);
            const errorMessage = error?.data?.errorMessage || error?.data?.message || error?.message || t('common.error');
            toast.current?.show({
                severity: 'error',
                summary: t('common.error'),
                detail: errorMessage,
                life: 3000
            });
        }
    };

    const handleUndoAccounting = async () => {
        if (repairCloseInvoiceId == null) {
            toast.current?.show({
                severity: 'warn',
                summary: t('common.warning'),
                detail: t('warranty.customerDeclaration.repairClose.invoiceIdNotFound') || 'Fatura bilgisi bulunamadı',
                life: 3000
            });
            return;
        }

        try {
            await undoRepairCloseAccounting(repairCloseInvoiceId).unwrap();

            toast.current?.show({
                severity: 'success',
                summary: t('common.success'),
                detail: t('warranty.customerDeclaration.repairClose.undoAccountingSuccess') || 'Muhasebe işlemi geri alındı',
                life: 3000
            });

            // Close dialog after success
            setTimeout(() => {
                onHide();
            }, 1000);
        } catch (error) {
            console.error('Muhasebe geri alma hatası:', error);
            const errorMessage = error?.data?.errorMessage || error?.data?.message || error?.message || t('common.error');
            toast.current?.show({
                severity: 'error',
                summary: t('common.error'),
                detail: errorMessage,
                life: 3000
            });
        }
    };

    // Check if form should be disabled
    const isFormDisabled = isAccounting;

    const kdvliTutar = useMemo(() => {
        if (!isDomestic || totalPrice == null || totalPrice === 0 || kdvRate == null) return null;
        const rate = parseFloat(kdvRate) || 0;
        const kdvAmount = (totalPrice * rate) / 100;
        return totalPrice + kdvAmount;
    }, [isDomestic, totalPrice, kdvRate]);

    // Check if "Complete Invoice" button should be enabled
    // Only show if invoice is created, not in accounting, and no unsaved changes
    const canCompleteInvoice = isInvoiceCreated && !isAccounting && !hasUnsavedChanges;

    const footer = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex gap-2">
                {canCompleteInvoice && (
                    <Button
                        label={t('warranty.customerDeclaration.repairClose.completeInvoice') || 'Faturayı Tamamla'}
                        icon="pi pi-check-circle"
                        onClick={handleCompleteInvoice}
                        loading={isCompletingInvoice}
                        disabled={isCompletingInvoice}
                        severity="success"
                    />
                )}
                {isAccounting && (
                    <Button
                        label={t('warranty.customerDeclaration.repairClose.undoAccounting') || 'Muhasebeyi Geri Al'}
                        icon="pi pi-undo"
                        onClick={handleUndoAccounting}
                        loading={isUndoingAccounting}
                        disabled={isUndoingAccounting}
                        severity="warning"
                    />
                )}
            </div>
            <div className="flex gap-2">
                <Button label={t('common.close')} icon="pi pi-times" onClick={onHide} className="p-button-text" disabled={isSaving || isCompletingInvoice} />
                {!isAccounting && <Button label={t('common.save')} icon="pi pi-check" onClick={handleSave} loading={isSaving} disabled={isSaving || isCompletingInvoice} />}
            </div>
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={onHide}
                header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{t('warranty.customerDeclaration.repairClose.dialogTitle') || 'Onarım Kapanış'}</span>
                        <span style={{ color: '#666', fontSize: '0.9rem' }}>{t('warranty.customerDeclaration.repairClose.title') || 'Garanti Talep Detayları'}</span>
                    </div>
                }
                style={{ width: '90vw' }}
                footer={footer}
                maximizable
            >
                {/* Form Fields */}
                <div className="grid mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="customerId" className="block mb-2 font-semibold">
                                <span className="text-red-500">* </span>
                                {t('warranty.customerDeclaration.repairClose.customer') || 'Müşteri'}
                            </label>
                            <Dropdown
                                id="customerId"
                                value={customerId}
                                onChange={handleCustomerChange}
                                options={customerOptions}
                                optionLabel="label"
                                optionValue="value"
                                placeholder={t('warranty.customerDeclaration.repairClose.customerPlaceholder') || 'Müşteri seçiniz'}
                                className="w-full"
                                disabled={isFormDisabled}
                                filter
                                filterTemplate={customerFilterTemplate}
                                showClear
                                loading={isLoadingCustomers}
                                appendTo="self"
                                emptyMessage={t('common.noRecords') || 'Kayıt bulunamadı'}
                            />
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="invoiceNumber" className="block mb-2 font-semibold">
                                {t('warranty.customerDeclaration.repairClose.invoiceNumber') || 'Fatura Numarası'}
                            </label>
                            <InputText
                                id="invoiceNumber"
                                value={invoiceNumber}
                                onChange={(e) => {
                                    setInvoiceNumber(e.target.value);
                                    setHasUnsavedChanges(true);
                                }}
                                placeholder={t('warranty.customerDeclaration.repairClose.invoiceNumberPlaceholder') || 'Fatura numarasını giriniz'}
                                className="w-full"
                                disabled={isFormDisabled}
                            />
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="invoiceDate" className="block mb-2 font-semibold">
                                <span className="text-red-500">* </span>
                                {t('warranty.customerDeclaration.repairClose.invoiceDate') || 'Fatura Tarihi'}
                            </label>
                            <Calendar
                                id="invoiceDate"
                                value={invoiceDate}
                                onChange={(e) => {
                                    setInvoiceDate(e.value);
                                    setHasUnsavedChanges(true);
                                }}
                                placeholder={t('warranty.customerDeclaration.repairClose.invoiceDatePlaceholder') || 'Fatura tarihi seçiniz'}
                                dateFormat="dd/mm/yy"
                                className="w-full"
                                showIcon
                                disabled={isFormDisabled}
                            />
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="maturityDate" className="block mb-2 font-semibold">
                                {t('warranty.customerDeclaration.repairClose.maturityDate') || 'Vade Tarihi'}
                            </label>
                            <Calendar
                                id="maturityDate"
                                value={maturityDate}
                                onChange={(e) => {
                                    setMaturityDate(e.value);
                                    setHasUnsavedChanges(true);
                                }}
                                placeholder={t('warranty.customerDeclaration.repairClose.maturityDatePlaceholder') || 'Vade tarihi seçiniz'}
                                dateFormat="dd/mm/yy"
                                className="w-full"
                                showIcon
                                disabled={isFormDisabled}
                            />
                        </div>
                    </div>
                </div>

                {isAccounting && (
                    <div className="mb-3 p-3" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px' }}>
                        <i className="pi pi-info-circle mr-2" style={{ color: '#856404' }}></i>
                        <span style={{ color: '#856404', fontWeight: 'bold' }}>{t('warranty.customerDeclaration.repairClose.accountingCompleted') || 'Bu işlem tamamlanmıştır. Değişiklik yapılamaz.'}</span>
                    </div>
                )}

                <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} style={{ textAlign: 'center' }}>
                    {/* Labors Tab */}
                    <TabPanel header={t('warranty.customerDeclaration.repairClose.laborsTab') || 'İşçilikler'} headerStyle={{ justifyContent: 'center' }}>
                        <DataTable value={labors} dataKey="_rowKey" emptyMessage={t('common.noRecords') || 'Kayıt bulunamadı'} editMode={isFormDisabled ? null : 'cell'} className="editable-cells-table" showGridlines stripedRows>
                            <Column field="laborCode" header={t('warranty.customerDeclaration.repairClose.laborCode') || 'İşçilik Kodu'} style={{ width: '15%' }} />
                            <Column field="description" header={t('warranty.customerDeclaration.repairClose.laborName') || 'İşçilik Adı'} style={{ width: '25%' }} />
                            <Column field="laborTime" header={t('warranty.customerDeclaration.repairClose.laborTime') || 'Süre'} style={{ width: '10%', textAlign: 'center' }} />
                            <Column field="laborCurrency" header={t('warranty.customerDeclaration.repairClose.currency') || 'Para Birimi'} style={{ width: '10%', textAlign: 'center' }} />
                            <Column field="laborPrice" header={t('warranty.customerDeclaration.repairClose.price') || 'Fiyat'} body={(rowData) => (rowData.laborPrice || 0).toFixed(2)} style={{ width: '12%', textAlign: 'right' }} />
                            <Column
                                field="laborDiscountRate"
                                header={t('warranty.customerDeclaration.repairClose.discountRate') || 'İndirim %'}
                                body={(rowData) => `${(rowData.laborDiscountRate || 0).toFixed(2)}%`}
                                editor={!isFormDisabled ? (options) => discountEditor(options) : null}
                                onCellEditComplete={!isFormDisabled ? onLaborCellEditComplete : null}
                                style={{ width: '14%', textAlign: 'right', backgroundColor: isFormDisabled ? '#e9ecef' : '#f0f8ff', cursor: isFormDisabled ? 'default' : 'pointer' }}
                            />
                            <Column
                                field="sellingPrice"
                                header={t('warranty.customerDeclaration.repairClose.sellingPrice') || 'Satış Fiyatı'}
                                body={(rowData) => (rowData.sellingPrice || 0).toFixed(2)}
                                editor={!isFormDisabled ? (options) => priceEditor(options) : null}
                                onCellEditComplete={!isFormDisabled ? onLaborCellEditComplete : null}
                                style={{ width: '14%', textAlign: 'right', backgroundColor: isFormDisabled ? '#e9ecef' : '#f0f8ff', cursor: isFormDisabled ? 'default' : 'pointer' }}
                            />
                        </DataTable>
                    </TabPanel>

                    {/* Parts Tab */}
                    <TabPanel header={t('warranty.customerDeclaration.repairClose.partsTab') || 'Parçalar'} headerStyle={{ justifyContent: 'center' }}>
                        <DataTable value={parts} dataKey="_rowKey" emptyMessage={t('common.noRecords') || 'Kayıt bulunamadı'} editMode={isFormDisabled ? null : 'cell'} className="editable-cells-table" showGridlines stripedRows>
                            <Column field="itemCode" header={t('warranty.customerDeclaration.repairClose.itemCode') || 'Parça Kodu'} style={{ width: '15%' }} />
                            <Column field="description" header={t('warranty.customerDeclaration.repairClose.partName') || 'Parça Adı'} style={{ width: '25%' }} />
                            <Column field="quantity" header={t('warranty.customerDeclaration.repairClose.quantity') || 'Miktar'} style={{ width: '10%', textAlign: 'center' }} />
                            <Column field="laborCurrency" header={t('warranty.customerDeclaration.repairClose.currency') || 'Para Birimi'} style={{ width: '10%', textAlign: 'center' }} />
                            <Column field="itemPrice" header={t('warranty.customerDeclaration.repairClose.price') || 'Fiyat'} body={(rowData) => (rowData.itemPrice || 0).toFixed(2)} style={{ width: '12%', textAlign: 'right' }} />
                            <Column
                                field="partsDiscountRate"
                                header={t('warranty.customerDeclaration.repairClose.discountRate') || 'İndirim %'}
                                body={(rowData) => `${(rowData.partsDiscountRate || 0).toFixed(2)}%`}
                                editor={!isFormDisabled ? (options) => discountEditor(options) : null}
                                onCellEditComplete={!isFormDisabled ? onPartCellEditComplete : null}
                                style={{ width: '14%', textAlign: 'right', backgroundColor: isFormDisabled ? '#e9ecef' : '#f0f8ff', cursor: isFormDisabled ? 'default' : 'pointer' }}
                            />
                            <Column
                                field="sellingPrice"
                                header={t('warranty.customerDeclaration.repairClose.sellingPrice') || 'Satış Fiyatı'}
                                body={(rowData) => (rowData.sellingPrice || 0).toFixed(2)}
                                editor={!isFormDisabled ? (options) => priceEditor(options) : null}
                                onCellEditComplete={!isFormDisabled ? onPartCellEditComplete : null}
                                style={{ width: '14%', textAlign: 'right', backgroundColor: isFormDisabled ? '#e9ecef' : '#f0f8ff', cursor: isFormDisabled ? 'default' : 'pointer' }}
                            />
                        </DataTable>
                    </TabPanel>

                    {/* Other Costs Tab */}
                    <TabPanel header={t('warranty.customerDeclaration.repairClose.otherCostsTab') || 'Diğer Maliyetler'} headerStyle={{ justifyContent: 'center' }}>
                        <DataTable value={otherCosts} dataKey="_rowKey" emptyMessage={t('common.noRecords') || 'Kayıt bulunamadı'} editMode={isFormDisabled ? null : 'cell'} className="editable-cells-table" showGridlines stripedRows>
                            <Column field="costType" header={t('warranty.customerDeclaration.repairClose.costType') || 'Maliyet Tipi'} style={{ width: '30%' }} />
                            <Column field="description" header={t('warranty.customerDeclaration.repairClose.description') || 'Açıklama'} style={{ width: '35%' }} />
                            <Column field="currency" header={t('warranty.customerDeclaration.repairClose.currency') || 'Para Birimi'} style={{ width: '15%', textAlign: 'center' }} />
                            <Column field="price" header={t('warranty.customerDeclaration.repairClose.price') || 'Fiyat'} body={(rowData) => (rowData.price || 0).toFixed(2)} style={{ width: '12%', textAlign: 'right' }} />
                            <Column
                                field="sellingPrice"
                                header={t('warranty.customerDeclaration.repairClose.sellingPrice') || 'Fiyat'}
                                body={(rowData) => (rowData.sellingPrice || 0).toFixed(2)}
                                editor={!isFormDisabled ? (options) => priceEditor(options) : null}
                                onCellEditComplete={!isFormDisabled ? onOtherCostCellEditComplete : null}
                                style={{ width: '20%', textAlign: 'right', backgroundColor: isFormDisabled ? '#e9ecef' : '#f0f8ff', cursor: isFormDisabled ? 'default' : 'pointer' }}
                            />
                        </DataTable>
                    </TabPanel>
                </TabView>

                {/* Total Price Section */}
                <div className="mt-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', borderTop: '2px solid #dee2e6' }}>
                    <div className="grid">
                        <div className="col-12 md:col-6 md:col-offset-6">
                            <div className="field">
                                <label htmlFor="totalPrice" className="block mb-2 font-bold text-lg">
                                    {t('warranty.customerDeclaration.repairClose.totalInvoiceAmount') || 'Toplam Fatura Tutarı'}
                                </label>
                                <InputNumber
                                    id="totalPrice"
                                    value={totalPrice}
                                    onValueChange={(e) => {
                                        setTotalPrice(e.value || 0);
                                        setHasUnsavedChanges(true);
                                    }}
                                    mode="decimal"
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    min={0}
                                    className="w-full"
                                    inputStyle={{ fontSize: '1.2rem', fontWeight: 'bold', textAlign: 'right' }}
                                    placeholder="0.00"
                                    disabled={isFormDisabled}
                                />
                                {kdvliTutar != null && (
                                    <div className="mt-2 flex align-items-center gap-2 p-2 surface-100 border-round">
                                        <span className="font-semibold text-sm">
                                            {t('preAccounting.kdvliTutar') || 'KDV Dahil Tutar'} ({kdvRate}% KDV):
                                        </span>
                                        <span className="text-lg font-bold text-primary">
                                            {kdvliTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                )}
                                <small className="text-gray-600 block mt-1">{t('warranty.customerDeclaration.repairClose.totalPriceNote') || 'Not: Tablolardaki değerler değiştiğinde otomatik güncellenir. Manuel de düzenleyebilirsiniz.'}</small>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default RepairCloseDialog;
